import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { marketAPI, tradingAPI, accountAPI, paymentAPI } from '../lib/api';
import { formatCurrency, formatPercent, getChangeColor, getPnLColor } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  TrendingUp, TrendingDown, Wallet, History, User, LogOut, Menu, X, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Home, Search, 
  BarChart2, ListOrdered, Clock, MessageSquare, Shield, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip 
} from 'recharts';

function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { user, logout, updateUser } = useAuth();

  const handleAccountSwitch = async (type) => {
    try {
      const res = await accountAPI.switchAccount(type);
      updateUser(res.data);
      toast.success(`Switched to ${type.toUpperCase()} account`);
    } catch (error) {
      toast.error('Failed to switch account');
    }
  };

  // Admin emails list - should match backend ADMIN_EMAILS
  const ADMIN_EMAILS = ["support@geneiprofx.com"];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/wallet', label: 'Wallet', icon: <Wallet className="w-5 h-5" /> },
    { path: '/account', label: 'Account', icon: <User className="w-5 h-5" /> },
    { path: '/accounts', label: 'Manage Accounts', icon: <Users className="w-5 h-5" /> },
    // Admin Panel - only visible to admin users
    ...(isAdmin ? [{ path: '/admin', label: 'Admin Panel', icon: <Shield className="w-5 h-5" /> }] : []),
  ];

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}
      
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0f0f11] border-r border-zinc-800 transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-300 flex flex-col`}>
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-bold text-white font-['Outfit']">GeneiProFx</span>
            </Link>
            <button onClick={() => setOpen(false)} className="lg:hidden text-zinc-400"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 p-1 rounded-lg bg-zinc-900">
            <button onClick={() => handleAccountSwitch('demo')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${user?.account_type === 'demo' ? 'bg-primary text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="switch-demo-btn">Demo</button>
            <button onClick={() => handleAccountSwitch('live')} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${user?.account_type === 'live' ? 'bg-primary text-white' : 'text-zinc-400 hover:text-white'}`} data-testid="switch-live-btn">Live</button>
          </div>
        </div>

        <div className="p-4 border-b border-zinc-800">
          <div className="text-sm text-zinc-400 mb-1">{user?.account_type === 'demo' ? 'Demo Balance' : 'Live Balance'}</div>
          <div className="text-2xl font-mono font-semibold text-white" data-testid="balance-display">
            {formatCurrency(user?.account_type === 'demo' ? user?.demo_balance : user?.balance)}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link key={item.path} to={item.path} onClick={() => setOpen(false)} className={`nav-item ${location.pathname === item.path ? 'active' : ''}`} data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
              {item.icon}<span>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <button onClick={logout} className="nav-item w-full text-red-400 hover:bg-red-500/10" data-testid="logout-btn">
            <LogOut className="w-5 h-5" /><span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

import React from 'react';

// Performance: Memoized category colors
const CATEGORY_COLORS = {
  'Metals': 'text-yellow-400 bg-yellow-500/20',
  'Crypto': 'text-orange-400 bg-orange-500/20',
  'Indices': 'text-blue-400 bg-blue-500/20',
  'Energy': 'text-emerald-400 bg-emerald-500/20',
  'Stocks': 'text-purple-400 bg-purple-500/20'
};

// Performance: Memoized PriceCard to prevent unnecessary re-renders
const PriceCard = React.memo(function PriceCard({ pair, data, onSelect, selected }) {
  const isSelected = selected === pair;
  
  const formatPrice = (price) => {
    if (!price) return '0.00';
    if (price >= 100) return price.toFixed(2);
    return price.toFixed(4);
  };
  
  return (
    <button onClick={() => onSelect(pair)} className={`w-full p-3 rounded-lg text-left transition-all ${isSelected ? 'bg-primary/10 border border-primary/30' : 'glass-card hover:bg-zinc-800/50'}`} data-testid={`pair-${pair.replace('/', '-')}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-white text-sm">{pair}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded ${CATEGORY_COLORS[data?.category] || 'text-zinc-400 bg-zinc-500/20'}`}>{data?.category}</span>
        </div>
        <span className={`text-xs font-mono ${getChangeColor(data?.change || 0)}`}>{formatPercent(data?.change || 0)}</span>
      </div>
      <div className="text-xs text-zinc-500 mb-1">{data?.name}</div>
      <div className="flex items-center justify-between">
        <div className="font-mono text-sm text-white">{formatPrice(data?.mid)}</div>
        <div className="text-xs text-zinc-500">{formatPrice(data?.bid)} / {formatPrice(data?.ask)}</div>
      </div>
    </button>
  );
});

function TradePanel({ pair, price, onTrade }) {
  const { user } = useAuth();
  const [volume, setVolume] = useState('0.01');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState('market');
  const [pendingPrice, setPendingPrice] = useState('');
  const [stopLimitPrice, setStopLimitPrice] = useState('');

  // Default leverage is 1:100 for all users
  const DEFAULT_LEVERAGE = 100;
  
  // Check if user is logged in with investor password (read-only)
  const isInvestorSession = user?.session_type === 'investor';

  const orderTypes = [
    { value: 'market', label: 'Market Execution' },
    { value: 'buy_limit', label: 'Buy Limit' },
    { value: 'sell_limit', label: 'Sell Limit' },
    { value: 'buy_stop', label: 'Buy Stop' },
    { value: 'sell_stop', label: 'Sell Stop' },
    { value: 'buy_stop_limit', label: 'Buy Stop Limit' },
    { value: 'sell_stop_limit', label: 'Sell Stop Limit' },
  ];

  const handleTrade = async (direction) => {
    if (isInvestorSession) {
      toast.error('Trading not allowed with Investor password. Please login with Master password.');
      return;
    }
    if (!volume || parseFloat(volume) < 0.01) { toast.error('Minimum volume is 0.01 lots'); return; }
    
    // Validate pending order price
    if (orderType !== 'market' && !pendingPrice) {
      toast.error('Price is required for pending orders');
      return;
    }
    
    // Validate stop limit price for stop limit orders
    if ((orderType === 'buy_stop_limit' || orderType === 'sell_stop_limit') && !stopLimitPrice) {
      toast.error('Stop Limit price is required');
      return;
    }
    
    setLoading(true);
    try {
      await tradingAPI.openTrade({
        pair, 
        direction: orderType === 'market' ? direction : (orderType.includes('buy') ? 'buy' : 'sell'),
        volume: parseFloat(volume), 
        leverage: DEFAULT_LEVERAGE,
        stop_loss: stopLoss ? parseFloat(stopLoss) : null,
        take_profit: takeProfit ? parseFloat(takeProfit) : null,
        order_type: orderType,
        price: pendingPrice ? parseFloat(pendingPrice) : null,
        stop_limit_price: stopLimitPrice ? parseFloat(stopLimitPrice) : null,
      });
      
      if (orderType === 'market') {
        toast.success(`${direction.toUpperCase()} ${volume} lots ${pair} @ ${formatPrice(direction === 'buy' ? price?.ask : price?.bid)}`);
      } else {
        toast.success(`${orderType.replace('_', ' ').toUpperCase()} order placed @ ${pendingPrice}`);
      }
      onTrade();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to place order'); }
    finally { setLoading(false); }
  };

  const formatPrice = (p) => { if (!p) return '0.00'; if (p >= 100) return p.toFixed(2); return p.toFixed(4); };
  const balance = user?.demo_balance || user?.balance || 0;

  const lotSizes = ['0.01', '0.02', '0.03', '0.05', '0.1', '0.2', '0.5', '1.0'];
  
  // Determine if we show buy/sell buttons or single execute button
  const isPendingOrder = orderType !== 'market';
  const pendingDirection = orderType.includes('buy') ? 'buy' : 'sell';

  return (
    <div className="glass-card p-4 space-y-3" data-testid="trade-panel">
      {isInvestorSession && (
        <div className="p-2 rounded bg-yellow-500/10 border border-yellow-500/30 mb-2">
          <p className="text-yellow-400 text-xs text-center">
            👁️ Investor Mode (Read-Only) - Trading disabled
          </p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div><h3 className="text-base font-semibold text-white">{pair}</h3><div className="text-xs text-zinc-400">{price?.name}</div></div>
        <div className="text-right"><span className="font-mono text-lg text-white">{formatPrice(price?.mid)}</span></div>
      </div>

      {/* Order Type Selector */}
      <div>
        <Label className="text-zinc-400 text-[10px] mb-1.5 block">Order Type</Label>
        <select
          value={orderType}
          onChange={(e) => {
            setOrderType(e.target.value);
            if (e.target.value !== 'market' && !pendingPrice) {
              setPendingPrice(formatPrice(price?.mid));
            }
          }}
          disabled={isInvestorSession}
          className="w-full bg-zinc-900/50 border border-zinc-800 text-white rounded px-3 py-2 text-sm focus:border-primary focus:outline-none"
        >
          {orderTypes.map(type => (
            <option key={type.value} value={type.value} className="bg-zinc-900">
              {type.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-2 text-center">
        <div className="p-2 rounded bg-red-500/10 border border-red-500/20">
          <div className="text-[10px] text-zinc-400">SELL</div>
          <div className="font-mono text-sm text-red-400">{formatPrice(price?.bid)}</div>
        </div>
        <div className="p-2 rounded bg-green-500/10 border border-green-500/20">
          <div className="text-[10px] text-zinc-400">BUY</div>
          <div className="font-mono text-sm text-green-400">{formatPrice(price?.ask)}</div>
        </div>
      </div>

      {/* Pending Order Price Input */}
      {isPendingOrder && (
        <div>
          <Label className="text-zinc-400 text-[10px]">Price</Label>
          <Input 
            type="number" 
            value={pendingPrice} 
            onChange={(e) => setPendingPrice(e.target.value)} 
            placeholder="Enter price"
            step="0.0001"
            className="bg-zinc-900/50 border-zinc-800 text-white font-mono h-8 text-sm" 
            disabled={isInvestorSession} 
          />
          <p className="text-[9px] text-zinc-500 mt-1">
            {orderType === 'buy_limit' && 'Buy when price drops to this level'}
            {orderType === 'sell_limit' && 'Sell when price rises to this level'}
            {orderType === 'buy_stop' && 'Buy when price rises to this level'}
            {orderType === 'sell_stop' && 'Sell when price drops to this level'}
            {orderType === 'buy_stop_limit' && 'Place Buy Limit when price reaches stop'}
            {orderType === 'sell_stop_limit' && 'Place Sell Limit when price reaches stop'}
          </p>
        </div>
      )}

      {/* Stop Limit Price Input */}
      {(orderType === 'buy_stop_limit' || orderType === 'sell_stop_limit') && (
        <div>
          <Label className="text-zinc-400 text-[10px]">Stop Limit Price</Label>
          <Input 
            type="number" 
            value={stopLimitPrice} 
            onChange={(e) => setStopLimitPrice(e.target.value)} 
            placeholder="Limit price after stop"
            step="0.0001"
            className="bg-zinc-900/50 border-zinc-800 text-white font-mono h-8 text-sm" 
            disabled={isInvestorSession} 
          />
        </div>
      )}

      <div>
        <Label className="text-zinc-400 text-[10px] mb-1.5 block">Volume (Lots)</Label>
        <div className="flex flex-wrap gap-1.5" data-testid="volume-buttons">
          {lotSizes.map(lot => (
            <button
              key={lot}
              onClick={() => setVolume(lot)}
              disabled={isInvestorSession}
              className={`px-2.5 py-1.5 rounded text-xs font-mono transition-all ${
                volume === lot 
                  ? 'bg-primary text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
              } ${isInvestorSession ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`volume-${lot}`}
            >
              {lot}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-zinc-400 text-[10px]">Stop Loss</Label><Input type="number" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} placeholder="Optional" className="bg-zinc-900/50 border-zinc-800 text-white font-mono h-8 text-sm" disabled={isInvestorSession} /></div>
        <div><Label className="text-zinc-400 text-[10px]">Take Profit</Label><Input type="number" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} placeholder="Optional" className="bg-zinc-900/50 border-zinc-800 text-white font-mono h-8 text-sm" disabled={isInvestorSession} /></div>
      </div>

      <div className="flex justify-between text-[10px] text-zinc-500">
        <span>Balance: {formatCurrency(balance)}</span>
        <span>Leverage: 1:100</span>
      </div>

      {/* Buttons - Show Buy/Sell for market, single button for pending */}
      {orderType === 'market' ? (
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={() => handleTrade('sell')} disabled={loading || isInvestorSession} className="btn-sell py-4" data-testid="sell-btn">
            {loading ? <span className="spinner" /> : <><ArrowDownRight className="w-4 h-4 mr-1" />SELL BY MARKET</>}
          </Button>
          <Button onClick={() => handleTrade('buy')} disabled={loading || isInvestorSession} className="btn-buy py-4" data-testid="buy-btn">
            {loading ? <span className="spinner" /> : <><ArrowUpRight className="w-4 h-4 mr-1" />BUY BY MARKET</>}
          </Button>
        </div>
      ) : (
        <Button 
          onClick={() => handleTrade(pendingDirection)} 
          disabled={loading || isInvestorSession} 
          className={`w-full py-4 ${pendingDirection === 'buy' ? 'btn-buy' : 'btn-sell'}`}
        >
          {loading ? <span className="spinner" /> : (
            <>
              {pendingDirection === 'buy' ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
              PLACE {orderType.replace('_', ' ').toUpperCase()}
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// MT5-style Chart Trading Panel (Buy/Sell on Chart)
function ChartTradePanel({ pair, price, onTrade }) {
  const { user } = useAuth();
  const [volume, setVolume] = useState('0.01');
  const [loading, setLoading] = useState(false);

  const DEFAULT_LEVERAGE = 100;
  const isInvestorSession = user?.session_type === 'investor';

  const handleTrade = async (direction) => {
    if (isInvestorSession) {
      toast.error('Trading not allowed with Investor password. Please login with Master password.');
      return;
    }
    if (!volume || parseFloat(volume) < 0.01) { 
      toast.error('Minimum volume is 0.01 lots'); 
      return; 
    }
    setLoading(true);
    try {
      await tradingAPI.openTrade({
        pair, 
        direction, 
        volume: parseFloat(volume), 
        leverage: DEFAULT_LEVERAGE,
        stop_loss: null,
        take_profit: null,
      });
      const tradePrice = direction === 'buy' ? price?.ask : price?.bid;
      toast.success(`${direction.toUpperCase()} ${volume} lots ${pair} @ ${tradePrice?.toFixed(2)}`);
      onTrade();
    } catch (error) { 
      toast.error(error.response?.data?.detail || 'Failed to place order'); 
    }
    finally { setLoading(false); }
  };

  const formatPrice = (p) => { 
    if (!p) return '0.00'; 
    if (p >= 100) return p.toFixed(2); 
    return p.toFixed(4); 
  };

  const lotSizes = ['0.01', '0.02', '0.03', '0.05', '0.1', '0.2', '0.5', '1.0'];

  return (
    <div className="glass-card p-3 mt-3" data-testid="chart-trade-panel">
      {/* Volume Selection */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-zinc-400 text-xs">Volume:</span>
        <div className="flex flex-wrap gap-1">
          {lotSizes.map(lot => (
            <button
              key={lot}
              onClick={() => setVolume(lot)}
              disabled={isInvestorSession}
              className={`px-2 py-1 rounded text-xs font-mono transition-all ${
                volume === lot 
                  ? 'bg-primary text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              } ${isInvestorSession ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {lot}
            </button>
          ))}
        </div>
      </div>

      {isInvestorSession && (
        <div className="p-2 rounded bg-yellow-500/10 border border-yellow-500/30">
          <p className="text-yellow-400 text-xs text-center">👁️ Investor Mode - Trading disabled</p>
        </div>
      )}

      {/* Buy/Sell Buttons - MT5 Style */}
      <div className="grid grid-cols-2 gap-3">
        <button 
          onClick={() => handleTrade('sell')} 
          disabled={loading || isInvestorSession}
          className="flex flex-col items-center justify-center p-3 rounded-lg bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 transition-all disabled:opacity-50"
          data-testid="chart-sell-btn"
        >
          <span className="text-red-400 text-xs mb-1">SELL</span>
          <span className="text-red-400 font-mono text-lg font-bold">{formatPrice(price?.bid)}</span>
          <ArrowDownRight className="w-4 h-4 text-red-400 mt-1" />
        </button>
        
        <button 
          onClick={() => handleTrade('buy')} 
          disabled={loading || isInvestorSession}
          className="flex flex-col items-center justify-center p-3 rounded-lg bg-green-500/10 border border-green-500/30 hover:bg-green-500/20 transition-all disabled:opacity-50"
          data-testid="chart-buy-btn"
        >
          <span className="text-green-400 text-xs mb-1">BUY</span>
          <span className="text-green-400 font-mono text-lg font-bold">{formatPrice(price?.ask)}</span>
          <ArrowUpRight className="w-4 h-4 text-green-400 mt-1" />
        </button>
      </div>

      {/* Spread Info */}
      <div className="text-center mt-2 text-xs text-zinc-500">
        Spread: {((price?.ask || 0) - (price?.bid || 0)).toFixed(price?.mid >= 100 ? 2 : 4)} | Leverage: 1:100
      </div>
    </div>
  );
}

// MT5-style Account Metrics Header for Positions Tab
function AccountMetricsHeader({ metrics }) {
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const getPnLColor = (value) => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-white';
  };

  return (
    <div className="space-y-1 text-sm font-mono" data-testid="account-metrics">
      {/* Main Metrics */}
      <div className="flex justify-between items-center py-1">
        <span className="text-zinc-400">Balance:</span>
        <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.balance)}</span>
      </div>
      <div className="flex justify-between items-center py-1">
        <span className="text-zinc-400">Equity:</span>
        <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.equity)}</span>
      </div>
      <div className="flex justify-between items-center py-1">
        <span className="text-zinc-400">Credit:</span>
        <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.credit)}</span>
      </div>
      <div className="flex justify-between items-center py-1">
        <span className="text-zinc-400">Margin:</span>
        <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.margin)}</span>
      </div>
      <div className="flex justify-between items-center py-1">
        <span className="text-zinc-400">Free margin:</span>
        <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.free_margin)}</span>
      </div>
      <div className="flex justify-between items-center py-1">
        <span className="text-zinc-400">Margin Level (%):</span>
        <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.margin_level)}</span>
      </div>
      
      {/* MT5-style Breakdown */}
      <div className="border-t border-zinc-800 mt-2 pt-2">
        <div className="flex justify-between items-center py-1">
          <span className="text-zinc-400">Profit:</span>
          <span className={`font-medium tracking-wide ${getPnLColor(metrics?.profit)}`}>{formatNumber(metrics?.profit)}</span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-zinc-400">Deposit:</span>
          <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.deposit)}</span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-zinc-400">Withdrawal:</span>
          <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.withdrawal)}</span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-zinc-400">Swap:</span>
          <span className={`font-medium tracking-wide ${getPnLColor(-metrics?.swap)}`}>{formatNumber(metrics?.swap)}</span>
        </div>
        <div className="flex justify-between items-center py-1">
          <span className="text-zinc-400">Commission:</span>
          <span className="text-white font-medium tracking-wide">{formatNumber(metrics?.commission)}</span>
        </div>
      </div>
    </div>
  );
}

// MT5-style Position Item (matching the reference image exactly)
function MT5PositionItem({ trade, onClose, closing }) {
  const formatPrice = (p) => {
    if (!p) return '0.00';
    if (p >= 100) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return p.toFixed(4);
  };

  return (
    <div className="flex justify-between items-start py-3 border-b border-zinc-800/50 last:border-b-0">
      <div>
        <div className="flex items-center gap-2">
          <span className="text-white font-medium">{trade.pair},</span>
          <span className={trade.direction === 'buy' ? 'text-blue-400' : 'text-red-400'}>
            {trade.direction} {trade.volume?.toFixed(2)}
          </span>
        </div>
        <div className="text-zinc-400 text-sm font-mono mt-1">
          {formatPrice(trade.entry_price)} → {formatPrice(trade.current_price)}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`font-mono text-lg font-medium ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trade.pnl >= 0 ? '' : '-'}{Math.abs(trade.pnl).toFixed(2)}
        </span>
        <button 
          onClick={() => onClose(trade.id)} 
          disabled={closing === trade.id}
          className="text-zinc-500 hover:text-red-400 transition-colors p-1"
          data-testid={`close-position-${trade.id}`}
        >
          {closing === trade.id ? '...' : '✕'}
        </button>
      </div>
    </div>
  );
}

// Full Positions Tab Component (MT5 Style)
function PositionsTab({ trades, metrics, onRefresh }) {
  const [closing, setClosing] = useState(null);

  const handleClose = async (tradeId) => {
    setClosing(tradeId);
    try {
      await tradingAPI.closeTrade(tradeId);
      toast.success('Position closed');
      onRefresh();
    } catch (error) {
      toast.error('Failed to close position');
    } finally {
      setClosing(null);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#0a0a0b]" data-testid="positions-tab">
      {/* Account Metrics Header */}
      <div className="p-4 border-b border-zinc-800">
        <AccountMetricsHeader metrics={metrics} />
      </div>

      {/* Positions Section */}
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between p-4 pb-2">
          <h3 className="text-white font-medium">Positions</h3>
          <button className="text-zinc-500 hover:text-white">•••</button>
        </div>

        <ScrollArea className="h-[calc(100%-3rem)] px-4">
          {trades.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              No open positions
            </div>
          ) : (
            <div>
              {trades.map((trade) => (
                <MT5PositionItem 
                  key={trade.id} 
                  trade={trade} 
                  onClose={handleClose}
                  closing={closing}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}

// MT5-style History Summary Row
function HistorySummaryRow({ label, value, isProfit = false, isNegative = false }) {
  let valueColor = 'text-white';
  if (isProfit) {
    valueColor = value >= 0 ? 'text-green-400' : 'text-red-400';
  } else if (isNegative) {
    valueColor = 'text-red-400';
  }

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0.00';
    const absNum = Math.abs(num);
    const formatted = absNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (num < 0) return `-${formatted}`;
    return formatted;
  };

  return (
    <div className="flex justify-between items-center py-1.5 border-b border-zinc-800/50 last:border-b-0">
      <span className="text-zinc-400 text-sm">{label}:</span>
      <span className={`font-mono text-sm ${valueColor}`}>{formatNumber(value)}</span>
    </div>
  );
}

// MT5-style History Trade Item
function HistoryTradeItem({ trade }) {
  const formatPrice = (p) => {
    if (!p) return '0.00';
    if (p >= 100) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return p.toFixed(4);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(',', '');
  };

  return (
    <div className="flex justify-between items-start py-3 border-b border-zinc-800/50 last:border-b-0">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium">{trade.pair},</span>
          <span className={trade.direction === 'buy' ? 'text-blue-400' : 'text-red-400'}>
            {trade.direction} {trade.volume?.toFixed(2)}
          </span>
        </div>
        <div className="text-zinc-400 text-sm font-mono mt-0.5">
          {formatPrice(trade.entry_price)} → {formatPrice(trade.current_price)}
        </div>
      </div>
      <div className="text-right">
        <div className="text-zinc-500 text-xs mb-0.5">
          {formatDateTime(trade.closed_at)}
        </div>
        <div className={`font-mono font-medium ${trade.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {trade.pnl >= 0 ? '' : '-'}{Math.abs(trade.pnl).toFixed(2)}
        </div>
      </div>
    </div>
  );
}

// Full History Tab Component (MT5 Style)
function HistoryTab({ user }) {
  const [closedTrades, setClosedTrades] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('positions');
  const [summary, setSummary] = useState({
    profit: 0,
    credit: 0,
    deposit: 0,
    withdrawal: 0,
    swap: 0,
    commission: 0,
    balance: 0,
  });

  useEffect(() => {
    fetchHistoryData();
  }, []);

  const fetchHistoryData = async () => {
    try {
      const [tradesRes, transactionsRes] = await Promise.all([
        tradingAPI.getTradeHistory(),
        paymentAPI.getTransactions(),
      ]);
      
      setClosedTrades(tradesRes.data);
      setTransactions(transactionsRes.data);
      
      // Calculate summary
      const totalProfit = tradesRes.data.reduce((sum, t) => sum + (t.pnl || 0), 0);
      const totalCommission = tradesRes.data.reduce((sum, t) => sum + (t.commission || 0), 0);
      const totalSwap = tradesRes.data.reduce((sum, t) => sum + (t.swap || 0), 0);
      
      const deposits = transactionsRes.data
        .filter(t => t.type === 'deposit' && t.status === 'completed')
        .reduce((sum, t) => sum + t.amount, 0);
      
      const withdrawals = transactionsRes.data
        .filter(t => t.type === 'withdrawal')
        .reduce((sum, t) => sum + t.amount, 0);
      
      const balance = user?.account_type === 'demo' ? user?.demo_balance : user?.balance;
      
      setSummary({
        profit: totalProfit,
        credit: 0,
        deposit: deposits,
        withdrawal: -withdrawals,
        swap: -totalSwap,
        commission: totalCommission,
        balance: balance || 0,
      });
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const subTabs = [
    { id: 'positions', label: 'POSITIONS' },
    { id: 'orders', label: 'ORDERS' },
    { id: 'deals', label: 'DEALS' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#0a0a0b]" data-testid="history-tab">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800">
        <h2 className="text-white font-medium">History</h2>
        <span className="text-zinc-500 text-sm">All symbols</span>
      </div>

      {/* Sub Tabs */}
      <div className="flex border-b border-zinc-800">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeSubTab === tab.id
                ? 'text-white border-b-2 border-white'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary */}
      <div className="p-4 border-b border-zinc-800">
        <HistorySummaryRow label="Profit" value={summary.profit} isProfit />
        <HistorySummaryRow label="Credit" value={summary.credit} />
        <HistorySummaryRow label="Deposit" value={summary.deposit} />
        <HistorySummaryRow label="Withdrawal" value={summary.withdrawal} isNegative={summary.withdrawal < 0} />
        <HistorySummaryRow label="Swap" value={summary.swap} isNegative={summary.swap < 0} />
        <HistorySummaryRow label="Commission" value={summary.commission} />
        <HistorySummaryRow label="Balance" value={summary.balance} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full px-4">
          {activeSubTab === 'positions' && (
            closedTrades.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No closed positions</div>
            ) : (
              <div>
                {closedTrades.map((trade) => (
                  <HistoryTradeItem key={trade.id} trade={trade} />
                ))}
              </div>
            )
          )}
          {activeSubTab === 'orders' && (
            <div className="text-center py-8 text-zinc-500">No pending orders</div>
          )}
          {activeSubTab === 'deals' && (
            closedTrades.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No deals</div>
            ) : (
              <div>
                {closedTrades.map((trade) => (
                  <HistoryTradeItem key={trade.id} trade={trade} />
                ))}
              </div>
            )
          )}
        </ScrollArea>
      </div>
    </div>
  );
}

// Inline Positions for Dashboard (smaller version)
function MT5PositionsCompact({ trades, onRefresh }) {
  const [closing, setClosing] = useState(null);
  const handleClose = async (tradeId) => {
    setClosing(tradeId);
    try { await tradingAPI.closeTrade(tradeId); toast.success('Position closed'); onRefresh(); }
    catch (error) { toast.error('Failed to close'); }
    finally { setClosing(null); }
  };

  const formatPrice = (p) => { if (!p) return '0.00'; if (p >= 100) return p.toFixed(2); return p.toFixed(4); };

  const totalPnL = trades.reduce((sum, t) => sum + (t.pnl || 0), 0);

  if (trades.length === 0) {
    return <div className="text-center py-4 text-zinc-500 text-sm">No open positions</div>;
  }

  return (
    <div className="space-y-2" data-testid="open-positions">
      <div className="grid grid-cols-12 gap-1 text-[9px] text-zinc-500 px-2 font-medium">
        <div className="col-span-2">Symbol</div>
        <div className="col-span-1">Type</div>
        <div className="col-span-1">Vol</div>
        <div className="col-span-2">Open</div>
        <div className="col-span-2">Current</div>
        <div className="col-span-1">S/L</div>
        <div className="col-span-1">T/P</div>
        <div className="col-span-2 text-right">Profit</div>
      </div>

      <ScrollArea className="h-[180px]">
        <div className="space-y-1">
          {trades.map((trade) => (
            <div key={trade.id} className="relative grid grid-cols-12 gap-1 items-center p-2 pr-10 rounded bg-zinc-800/30 hover:bg-zinc-800/50 text-[10px] group">
              <div className="col-span-2 font-medium text-white truncate">{trade.pair}</div>
              <div className={`col-span-1 ${trade.direction === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                {trade.direction === 'buy' ? '▲ Buy' : '▼ Sell'}
              </div>
              <div className="col-span-1 font-mono text-zinc-300">{trade.volume?.toFixed(2)}</div>
              <div className="col-span-2 font-mono text-zinc-300">{formatPrice(trade.entry_price)}</div>
              <div className="col-span-2 font-mono text-white">{formatPrice(trade.current_price)}</div>
              <div className="col-span-1 font-mono text-zinc-400">{trade.stop_loss ? formatPrice(trade.stop_loss) : '-'}</div>
              <div className="col-span-1 font-mono text-zinc-400">{trade.take_profit ? formatPrice(trade.take_profit) : '-'}</div>
              <div className={`col-span-2 text-right font-mono font-medium ${getPnLColor(trade.pnl)}`}>
                <div>{trade.pnl >= 0 ? '+' : ''}{formatCurrency(trade.pnl)}</div>
                <div className="text-[8px] text-zinc-500">{trade.pnl_pips >= 0 ? '+' : ''}{trade.pnl_pips?.toFixed(1)} pips</div>
              </div>
              <button 
                onClick={() => handleClose(trade.id)} 
                disabled={closing === trade.id} 
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded bg-red-500/20 hover:bg-red-500/40 text-red-400 hover:text-red-300 text-xs font-bold transition-colors"
                data-testid={`close-trade-${trade.id}`}
              >
                {closing === trade.id ? '...' : '✕'}
              </button>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="flex justify-between items-center p-2 rounded bg-zinc-900/50 border-t border-zinc-800">
        <div className="text-xs text-zinc-400">{trades.length} position{trades.length !== 1 ? 's' : ''}</div>
        <div className={`font-mono font-semibold ${getPnLColor(totalPnL)}`}>
          Total: {totalPnL >= 0 ? '+' : ''}{formatCurrency(totalPnL)}
        </div>
      </div>
    </div>
  );
}

function NewsPanel({ news }) {
  const impactColors = { high: 'badge-danger', medium: 'badge-warning', low: 'badge-info' };
  return (
    <ScrollArea className="h-[200px]">
      <div className="space-y-2 pr-4">
        {news.map((item) => (
          <div key={item.id} className="p-2 rounded bg-zinc-800/30 hover:bg-zinc-800/50">
            <div className="flex items-center justify-between mb-1">
              <span className={`badge text-[9px] ${impactColors[item.impact]}`}>{item.impact.toUpperCase()}</span>
              <span className="text-[9px] text-zinc-500">{item.time}</span>
            </div>
            <h4 className="font-medium text-white text-xs">{item.title}</h4>
            <p className="text-[10px] text-zinc-400 line-clamp-2">{item.summary}</p>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

// MT5-style Bottom Navigation
function MT5BottomNav({ activeTab, setActiveTab }) {
  const tabs = [
    { id: 'quotes', label: 'Quotes', icon: <TrendingUp className="w-5 h-5" /> },
    { id: 'chart', label: 'Charts', icon: <BarChart2 className="w-5 h-5" /> },
    { id: 'trade', label: 'Trade', icon: <TrendingDown className="w-5 h-5" /> },
    { id: 'positions', label: 'Positions', icon: <ListOrdered className="w-5 h-5" /> },
    { id: 'history', label: 'History', icon: <Clock className="w-5 h-5" /> },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 lg:left-64 bg-[#0f0f11] border-t border-zinc-800 z-30" data-testid="mt5-bottom-nav">
      <div className="flex justify-around items-center h-16">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              activeTab === tab.id 
                ? 'text-blue-400' 
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.id === 'trade' ? (
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                activeTab === tab.id ? 'bg-blue-500/20' : 'bg-zinc-800'
              }`}>
                <TrendingUp className="w-6 h-6" />
              </div>
            ) : (
              <>
                {tab.icon}
                <span className="text-xs mt-1">{tab.label}</span>
              </>
            )}
          </button>
        ))}
      </div>
    </nav>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedPair, setSelectedPair] = useState('XAU/USD');
  const [prices, setPrices] = useState({});
  const [chartData, setChartData] = useState([]);
  const [openTrades, setOpenTrades] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('trade');
  const [metrics, setMetrics] = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);
  const [chartTimeframe, setChartTimeframe] = useState('H1');
  
  // Performance: Track if component is mounted
  const isMountedRef = useRef(true);
  const fetchingRef = useRef(false);

  // Performance: Optimized data fetching with less frequent updates
  const fetchData = useCallback(async (isInitial = false) => {
    // Prevent concurrent fetches
    if (fetchingRef.current && !isInitial) return;
    fetchingRef.current = true;
    
    try {
      // Fetch critical data first (prices and trades)
      const [pairsRes, tradesRes] = await Promise.all([
        marketAPI.getPairs(), 
        tradingAPI.getOpenTrades(),
      ]);
      
      if (!isMountedRef.current) return;
      
      const pricesMap = {};
      pairsRes.data.forEach((p) => { pricesMap[p.pair] = p; });
      setPrices(pricesMap);
      setOpenTrades(tradesRes.data);
      
      // Fetch secondary data only on initial load or less frequently
      if (isInitial) {
        const [newsRes, catsRes, metricsRes, statusRes] = await Promise.all([
          marketAPI.getNews(), 
          marketAPI.getCategories(),
          accountAPI.getMetrics(),
          marketAPI.getStatus(),
        ]);
        
        if (!isMountedRef.current) return;
        
        setNews(newsRes.data);
        setCategories(catsRes.data);
        setMetrics(metricsRes.data);
        setMarketStatus(statusRes.data);
      }
    } catch (error) { 
      console.error('Failed to fetch data:', error); 
    } finally { 
      if (isMountedRef.current) setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Performance: Separate metrics fetch (less frequent)
  const fetchMetrics = useCallback(async () => {
    try {
      const metricsRes = await accountAPI.getMetrics();
      if (isMountedRef.current) setMetrics(metricsRes.data);
    } catch (error) { 
      console.error('Failed to fetch metrics:', error); 
    }
  }, []);

  const fetchChart = useCallback(async () => {
    try { 
      const res = await marketAPI.getChart(selectedPair, 50, chartTimeframe); 
      if (isMountedRef.current) setChartData(res.data); 
    }
    catch (error) { console.error('Failed to fetch chart:', error); }
  }, [selectedPair, chartTimeframe]);

  // Performance: Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Performance: Initial fetch and optimized polling intervals
  useEffect(() => { 
    fetchData(true); // Initial fetch with all data
    
    // Price updates every 10 seconds (was 5 - reduced for performance)
    const priceInterval = setInterval(() => fetchData(false), 10000);
    
    // Metrics update every 30 seconds (separate, less critical)
    const metricsInterval = setInterval(fetchMetrics, 30000);
    
    return () => {
      clearInterval(priceInterval);
      clearInterval(metricsInterval);
    };
  }, [fetchData, fetchMetrics]);
  
  useEffect(() => { fetchChart(); }, [fetchChart]);

  // Performance: Memoize filtered pairs to prevent recalculation on every render
  const filteredPairs = useMemo(() => {
    return Object.keys(prices).filter((pair) => {
      const data = prices[pair];
      const matchesSearch = searchQuery === '' || 
        pair.toLowerCase().includes(searchQuery.toLowerCase()) || 
        data?.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || data?.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [prices, searchQuery, selectedCategory]);

  const formatChartPrice = useCallback((value) => { 
    if (value >= 1000) return value.toFixed(0); 
    if (value >= 1) return value.toFixed(2); 
    return value.toFixed(4); 
  }, []);
  
  // MT5-style timeframe options
  const timeframes = [
    { id: 'M1', label: 'M1' },
    { id: 'M5', label: 'M5' },
    { id: 'M15', label: 'M15' },
    { id: 'M30', label: 'M30' },
    { id: 'H1', label: 'H1' },
    { id: 'H4', label: 'H4' },
    { id: 'D1', label: 'D1' },
    { id: 'W1', label: 'W1' },
    { id: 'MN', label: 'MN' },
  ];

  if (loading) return <div className="min-h-screen bg-[#09090B] flex items-center justify-center"><div className="spinner" /></div>;

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'quotes':
        return (
          <div className="p-3 pb-20 h-[calc(100vh-3rem)] overflow-auto">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-white">Markets</h2>
                <span className="text-xs text-zinc-500">{filteredPairs.length}</span>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-zinc-900/50 border-zinc-800 text-white h-8 text-sm" data-testid="search-input" />
              </div>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => setSelectedCategory('all')} className={`px-2 py-0.5 rounded text-[10px] ${selectedCategory === 'all' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`}>All</button>
                {categories.map((cat) => (<button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-2 py-0.5 rounded text-[10px] ${selectedCategory === cat ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`}>{cat}</button>))}
              </div>
              <ScrollArea className="h-[calc(100vh-14rem)]">
                <div className="space-y-1 pr-2">
                  {filteredPairs.map((pair) => (<PriceCard key={pair} pair={pair} data={prices[pair]} selected={selectedPair} onSelect={(p) => { setSelectedPair(p); setActiveTab('chart'); }} />))}
                  {filteredPairs.length === 0 && <div className="text-center py-6 text-zinc-500 text-sm">No symbols found</div>}
                </div>
              </ScrollArea>
            </div>
          </div>
        );

      case 'chart':
        return (
          <div className="p-3 pb-20 h-[calc(100vh-3rem)] overflow-auto">
            <div className="glass-card p-3" data-testid="price-chart">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="text-base font-semibold text-white">{selectedPair} <span className="text-xs text-zinc-500">{prices[selectedPair]?.name}</span></h3>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xl text-white">{formatChartPrice(prices[selectedPair]?.mid || 0)}</span>
                    <span className={`font-mono text-sm ${getChangeColor(prices[selectedPair]?.change || 0)}`}>{formatPercent(prices[selectedPair]?.change || 0)}</span>
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className="text-zinc-500">H: <span className="text-white font-mono">{formatChartPrice(prices[selectedPair]?.high_24h || 0)}</span></div>
                  <div className="text-zinc-500">L: <span className="text-white font-mono">{formatChartPrice(prices[selectedPair]?.low_24h || 0)}</span></div>
                </div>
              </div>
              
              {/* MT5-style Timeframe Selector */}
              <div className="flex items-center gap-1 mb-2 bg-zinc-900/50 p-1 rounded-lg overflow-x-auto">
                {timeframes.map((tf) => (
                  <button
                    key={tf.id}
                    onClick={() => setChartTimeframe(tf.id)}
                    className={`px-2 py-1 text-xs font-medium rounded transition-all whitespace-nowrap ${
                      chartTimeframe === tf.id
                        ? 'bg-blue-600 text-white'
                        : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                    }`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>
              
              <div className="h-[320px] min-h-[320px] relative" style={{ minWidth: '100%' }}>
                <ResponsiveContainer width="100%" height={320}>
                  <AreaChart data={chartData}>
                    <defs><linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="time" stroke="#52525B" tick={{ fill: '#71717A', fontSize: 9 }} tickFormatter={(v) => {
                      const date = new Date(v);
                      if (['D1', 'W1', 'MN'].includes(chartTimeframe)) {
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }
                      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                    }} />
                    <YAxis stroke="#52525B" tick={{ fill: '#71717A', fontSize: 9 }} domain={['auto', 'auto']} tickFormatter={formatChartPrice} />
                    <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '11px' }} labelFormatter={(v) => new Date(v).toLocaleString()} formatter={(v) => [formatChartPrice(v), 'Price']} />
                    <Area type="monotone" dataKey="close" stroke="#3B82F6" fillOpacity={1} fill="url(#colorPrice)" isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* MT5-style Chart Trading Panel */}
            <ChartTradePanel pair={selectedPair} price={prices[selectedPair]} onTrade={fetchData} />
          </div>
        );

      case 'trade':
        return (
          <div className="p-3 pb-20 grid grid-cols-12 gap-3 h-[calc(100vh-3rem)] overflow-auto">
            {/* Markets */}
            <div className="col-span-12 lg:col-span-3 space-y-2">
              <div className="flex items-center justify-between"><h2 className="text-base font-semibold text-white">Markets</h2><span className="text-xs text-zinc-500">{filteredPairs.length}</span></div>
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" /><Input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-zinc-900/50 border-zinc-800 text-white h-8 text-sm" data-testid="search-input-trade" /></div>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => setSelectedCategory('all')} className={`px-2 py-0.5 rounded text-[10px] ${selectedCategory === 'all' ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`}>All</button>
                {categories.map((cat) => (<button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-2 py-0.5 rounded text-[10px] ${selectedCategory === cat ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400'}`}>{cat}</button>))}
              </div>
              <ScrollArea className="h-[calc(100vh-16rem)]">
                <div className="space-y-1 pr-2">
                  {filteredPairs.map((pair) => (<PriceCard key={pair} pair={pair} data={prices[pair]} selected={selectedPair} onSelect={setSelectedPair} />))}
                  {filteredPairs.length === 0 && <div className="text-center py-6 text-zinc-500 text-sm">No symbols found</div>}
                </div>
              </ScrollArea>
            </div>

            {/* Chart & Positions */}
            <div className="col-span-12 lg:col-span-6 space-y-3">
              <div className="glass-card p-3" data-testid="price-chart">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="text-base font-semibold text-white">{selectedPair} <span className="text-xs text-zinc-500">{prices[selectedPair]?.name}</span></h3>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xl text-white">{formatChartPrice(prices[selectedPair]?.mid || 0)}</span>
                      <span className={`font-mono text-sm ${getChangeColor(prices[selectedPair]?.change || 0)}`}>{formatPercent(prices[selectedPair]?.change || 0)}</span>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="text-zinc-500">H: <span className="text-white font-mono">{formatChartPrice(prices[selectedPair]?.high_24h || 0)}</span></div>
                    <div className="text-zinc-500">L: <span className="text-white font-mono">{formatChartPrice(prices[selectedPair]?.low_24h || 0)}</span></div>
                  </div>
                </div>
                
                {/* MT5-style Timeframe Selector */}
                <div className="flex items-center gap-1 mb-2 bg-zinc-900/50 p-1 rounded-lg overflow-x-auto">
                  {timeframes.map((tf) => (
                    <button
                      key={tf.id}
                      onClick={() => setChartTimeframe(tf.id)}
                      className={`px-2 py-1 text-xs font-medium rounded transition-all whitespace-nowrap ${
                        chartTimeframe === tf.id
                          ? 'bg-blue-600 text-white'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                      }`}
                    >
                      {tf.label}
                    </button>
                  ))}
                </div>
                
                <div className="h-[200px] min-h-[200px]" style={{ minWidth: '100%' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={chartData}>
                      <defs><linearGradient id="colorPrice2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis dataKey="time" stroke="#52525B" tick={{ fill: '#71717A', fontSize: 9 }} tickFormatter={(v) => {
                        const date = new Date(v);
                        if (['D1', 'W1', 'MN'].includes(chartTimeframe)) {
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }
                        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                      }} />
                      <YAxis stroke="#52525B" tick={{ fill: '#71717A', fontSize: 9 }} domain={['auto', 'auto']} tickFormatter={formatChartPrice} />
                      <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '8px', color: '#FAFAFA', fontSize: '11px' }} labelFormatter={(v) => new Date(v).toLocaleString()} formatter={(v) => [formatChartPrice(v), 'Price']} />
                      <Area type="monotone" dataKey="close" stroke="#3B82F6" fillOpacity={1} fill="url(#colorPrice2)" isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="glass-card p-3">
                <h3 className="text-sm font-semibold text-white mb-2">Open Positions</h3>
                <MT5PositionsCompact trades={openTrades} onRefresh={fetchData} />
              </div>
            </div>

            {/* Trade Panel & News */}
            <div className="col-span-12 lg:col-span-3 space-y-3">
              <TradePanel pair={selectedPair} price={prices[selectedPair]} onTrade={fetchData} />
              <div className="glass-card p-3">
                <h3 className="text-sm font-semibold text-white mb-2">Market News</h3>
                <NewsPanel news={news} />
              </div>
            </div>
          </div>
        );

      case 'positions':
        return (
          <div className="pb-20 h-[calc(100vh-3rem)]">
            <PositionsTab trades={openTrades} metrics={metrics} onRefresh={fetchData} />
          </div>
        );

      case 'history':
        return (
          <div className="pb-20 h-[calc(100vh-3rem)]">
            <HistoryTab user={user} />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="dashboard-page">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <main className="flex-1 min-w-0">
        <header className="h-12 border-b border-zinc-800 flex items-center justify-between px-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-zinc-400 hover:text-white"><Menu className="w-6 h-6" /></button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white capitalize">{activeTab}</span>
            {activeTab === 'trade' && <span className="text-xs text-zinc-500">• {user?.account_type?.toUpperCase()}</span>}
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-zinc-400 hover:text-white" data-testid="refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </header>

        {renderTabContent()}
        
        {/* MT5 Bottom Navigation */}
        <MT5BottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
      </main>
    </div>
  );
}
